import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { BasicInformationBaseComponent } from 'src/app/base-components/basic-information-base.component';
import { EmploymentAgreementDto } from 'src/app/models/dtos/employment-agreement-dto';
import { EmploymentConditionsConfirmationService } from 'src/app/data/employment-conditions-confirmation.service';
import { combineLatest, distinctUntilChanged, EMPTY, first, firstValueFrom, forkJoin, startWith, switchMap, takeUntil } from 'rxjs';
import { LegalizationDataForWorkerAgreementDto } from 'src/app/models/dtos/worker-agreements/legalization-data-for-worker-agreement-dto';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { Messages } from '../../../common/enums';

@Component({
  selector: 'app-multiple-basic-information-step',
  templateUrl: './multiple-basic-information-step.component.html',
  styleUrls: ['./multiple-basic-information-step.component.scss'],
  standalone: false
})
export class MultipleBasicInformationStepComponent extends BasicInformationBaseComponent implements OnInit, OnChanges, OnDestroy {
  private employmentConditionsConfirmationService = inject(EmploymentConditionsConfirmationService);

  @Input() isExtension: boolean;
  @Input() selectedWorkers: any[];
  @Output() formGroupChange: EventEmitter<EmploymentAgreementDto> = new EventEmitter();
  @Output() selectedWorkersChange = new EventEmitter<any[]>();

  foreignWorkers = new Set<number>();
  workerAgreementLegalizationData: LegalizationDataForWorkerAgreementDto[] | null = null;

  constructor() {
    super();

    this.employmentTypeId = this.route.snapshot.params.employmentTypeId;
    this.listOfAgreementTypes$ = this.dictionaryService.getAgreementTypes([this.employmentTypeId]);
  }

  get foreignWorkersWithData(): Array<{
    id: number;
    name: string;
    legalizationData: LegalizationDataForWorkerAgreementDto;
  }> {
    if (!this.workerAgreementLegalizationData || this.foreignWorkers.size === 0) {
      return [];
    }

    return this.selectedWorkers
      .filter((worker) => this.foreignWorkers.has(worker.Id))
      .map((worker) => {
        const legalizationData = this.workerAgreementLegalizationData!.find((data) => data.WorkerId === worker.Id);
        return {
          id: worker.Id,
          name: worker.Name,
          legalizationData,
        };
      })
      .filter((item) => item.legalizationData);
  }

  get isSubmitForLegalizationEnabled(): boolean {
    const formData = this.basicInformationStepFormGroup.getRawValue();
    return !!(formData.company && formData.employer && formData.employerObject);
  }

  ngOnInit(): void {
    this.onAgreementTypeChange();
    this.onCompanyChange();
    this.onLocationChange();
    this.onSupervisorChange();
    this.onTemporaryWorkplaceChange();
    this.onJobTitleChange();
    this.initEmployerAutoCompletes();
    this.onBasicInformationStepFormGroupChange();
    this.onEmployerChange();
    this.onEmployerOrCompanyChange();
    this.setLegalizationInformation();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.onPrefetchedAgreementChanges(changes);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  setLegalizationInformation(): void {
    if (this.isPermittedToLegalization()) {
      this.workerService.getWorkerDataForAgreementForm(this.selectedWorkers.map((w) => w.Id))
        .pipe(first())
        .subscribe(async (res) => {
          this.foreignWorkers = new Set(res.filter((worker) => worker.IsForeigner).map((worker) => worker.WorkerId));
          this.workerAgreementLegalizationData = res.map((dto) => ({
            WorkerId: dto.WorkerId,
            LegalizationStatusName: null,
            LegalizationStatusId: null,
          }));

          const companies = await firstValueFrom(this.listOfCompanies$);
          this.selectedCompany = companies.find((c) => c.Id === this.company.value) || null;
        });
    }
  }

  submit = () => {
    this.formGroupChange.emit(this.updateBasicInformationStep(this.basicInformationStepFormGroup.getRawValue()));
  };

  isLegalizationFieldVisible(): boolean {
    return this.foreignWorkers.size > 0 && this.selectedCompany?.HasLegalization;
  }

  onSubmitForLegalization(): void {
    if (!this.isSubmitForLegalizationEnabled) {
      this.markRequiredLegalizationFieldsAsInvalid();
      return;
    }

    this.dialog
      .open(ConfirmDialogComponent, { data: { message: Messages.ConfirmSubmitForMultipleLegalization } })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => (isConfirmed ? super.submitForMultipleLegalizations(this.selectedWorkers.map((w) => w.Id)) : EMPTY)),
      )
      .subscribe((processedWorkerIds: number[]) => {
        if (processedWorkerIds && processedWorkerIds.length > 0) {
          this.selectedWorkers = this.selectedWorkers.filter((worker) => !processedWorkerIds.includes(worker.Id));

          if (this.selectedWorkers.length > 0) {
            sessionStorage.setItem('selectedWorkers', JSON.stringify(this.selectedWorkers));
            this.selectedWorkersChange.emit(this.selectedWorkers);
            this.foreignWorkers = new Set<number>([...this.foreignWorkers].filter((num) => !processedWorkerIds.includes(num)));
          } else {
            sessionStorage.removeItem('selectedWorkers');
            this.router.navigate(['/workerAgreements']);
          }
        }
      });
  }

  private markRequiredLegalizationFieldsAsInvalid(): void {
    const requiredFields = ['company', 'employer', 'employerObject'];

    requiredFields.forEach((fieldName) => {
      const control = this.basicInformationStepFormGroup.get(fieldName);
      if (!control.value) {
        control.setErrors({ required: true });
        control.markAsTouched();
      }
    });
  }

  private onEmployerOrCompanyChange(): void {
    combineLatest([this.employer.valueChanges.pipe(startWith(this.employer.value)), this.company.valueChanges.pipe(startWith(this.company.value))])
      .pipe(distinctUntilChanged())
      .pipe(
        switchMap(([employerId, companyId]) =>
          this.employmentConditionsConfirmationService.checkEmploymentConditionsConfirmations(employerId, companyId),
        ),
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((hasEmploymentConditionsConfirmations) => {
        this.hasEmploymentConditionsConfirmations.setValue(hasEmploymentConditionsConfirmations);
      });
  }
}
